<template>
  <AlertDialog v-model:open="isOpen">
    <template v-if="slots.trigger">
      <slot name="trigger" :open="openDialog" />
    </template>
    <AlertDialogTrigger v-else-if="slots.default" as-child>
      <slot />
    </AlertDialogTrigger>

    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>
          {{ t("pages.organizations.deleteDialog.title") }}
        </AlertDialogTitle>
        <AlertDialogDescription>
          <p>
            {{
              t("pages.organizations.deleteDialog.description", {
                name: organization?.name
              })
            }}
          </p>
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <AlertDialogCancel>
          {{ t("pages.organizations.deleteDialog.cancelButton") }}
        </AlertDialogCancel>
        <AlertDialogAction
          :disabled="isLoading"
          class="bg-red-600 hover:bg-red-700 focus:ring-red-600"
          @click="handleConfirm"
        >
          <LoadingIndicator v-if="isLoading" class="mr-2" />
          <Trash v-else class="w-4 h-4 mr-2" />
          {{ t("pages.organizations.deleteDialog.confirmButton") }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<script setup lang="ts">
import { deleteOrganization, useApiOrganizations } from "~/composables/api";
import { successToast, errorToast } from "~/components/ui/toast";
import type { ApiOrganization } from "~/server/api/organizations/index.get";
import { Trash } from "lucide-vue-next";

interface Props {
  organization: ApiOrganization | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{ deleted: [id: number] }>();

const isOpen = ref(false);
const isLoading = ref(false);
const { t } = useI18n();
const slots = useSlots();

const openDialog = () => {
  if (props.organization) {
    isOpen.value = true;
  }
};

defineExpose({
  open: openDialog
});

const { refresh } = await useApiOrganizations();

const handleConfirm = async () => {
  if (!props.organization) return;

  isLoading.value = true;
  try {
    await deleteOrganization(props.organization.id);
    successToast({
      title: t("pages.organizations.deleteDialog.success.title"),
      description: t("pages.organizations.deleteDialog.success.description", {
        name: props.organization.name
      })
    });
    await refresh();
    emit("deleted", props.organization.id);
    isOpen.value = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error("Failed to delete organization:", error);
    errorToast({
      title: t("pages.organizations.deleteDialog.error.title"),
      description:
        error?.data?.statusMessage ||
        t("pages.organizations.deleteDialog.error.description")
    });
  } finally {
    isLoading.value = false;
  }
};

watch(isOpen, (open) => {
  if (!open) {
    isLoading.value = false;
  }
});
</script>
